import React from 'react';
import { 
  Mail, 
  MapPin, 
  Phone, 
  Facebook, 
  Instagram, 
  Youtube, 
  ArrowRight,
  ShieldCheck,
  Leaf,
  Award,
  Truck,
  Clock
} from 'lucide-react';

const colors = {
  primary: '#FF8A00',
  primaryDark: '#FF6A00',
  textMuted: '#555555',
  borderSoft: 'rgba(255, 138, 0, 0.16)',
  gradientSummer: 'linear-gradient(135deg, #FF8A00 0%, #FFB45A 100%)',
  bgDark: '#111',
};

function Footer({ setCurrentPage }) {
  return (
    <footer style={{ 
      background: '#111', 
      color: 'white',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: '-100px',
        right: '-100px',
        width: '300px',
        height: '300px',
        background: 'rgba(255, 138, 0, 0.05)',
        borderRadius: '50%',
        filter: 'blur(60px)'
      }}></div>
      <div style={{
        position: 'absolute',
        bottom: '-50px',
        left: '-50px',
        width: '200px',
        height: '200px',
        background: 'rgba(255, 138, 0, 0.03)',
        borderRadius: '50%',
        filter: 'blur(60px)'
      }}></div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '6rem 2rem 3rem', position: 'relative', zIndex: 1 }}>
        
        {/* Newsletter Section - Summer Beat Style */}
        <div style={{
          background: 'linear-gradient(135deg, #2A2A2A 0%, #1A1A1A 100%)',
          padding: '3rem',
          borderRadius: '24px',
          marginBottom: '5rem',
          border: '1px solid rgba(255, 138, 0, 0.2)',
          textAlign: 'center',
          boxShadow: '0 30px 60px rgba(0, 0, 0, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative Elements */}
          <div style={{
            position: 'absolute',
            top: '0',
            right: '0',
            width: '100px',
            height: '100px',
            background: colors.gradientSummer,
            borderRadius: '0 0 0 100%',
            opacity: 0.1
          }}></div>
          
          <h3 style={{ 
            fontSize: '2.2rem', 
            fontWeight: '800', 
            marginBottom: '1rem',
            letterSpacing: '-0.5px',
            background: colors.gradientSummer,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Stay Updated with Summer Beat
          </h3>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.85)', 
            marginBottom: '2rem',
            fontSize: '1.1rem',
            maxWidth: '600px',
            margin: '0 auto 2rem',
            lineHeight: 1.6
          }}>
            Get exclusive offers, new product launches, and seasonal maintenance tips
          </p>
          <div style={{ 
            display: 'flex', 
            gap: '1rem',
            maxWidth: '500px',
            margin: '0 auto',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <input 
              type="email" 
              placeholder="Enter your email" 
              style={{
                flex: '1',
                minWidth: '250px',
                padding: '1rem 1.5rem',
                borderRadius: '12px',
                border: '2px solid rgba(255, 138, 0, 0.3)',
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                fontSize: '1rem',
                outline: 'none',
                transition: 'all 0.3s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.primary;
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.boxShadow = '0 0 0 4px rgba(255, 138, 0, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 138, 0, 0.3)';
                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                e.target.style.boxShadow = 'none';
              }}
            />
            <button style={{
              background: colors.gradientSummer,
              color: 'white',
              padding: '1rem 2.5rem',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '1rem',
              transition: 'all 0.3s',
              boxShadow: '0 10px 30px rgba(255, 138, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 15px 40px rgba(255, 138, 0, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(255, 138, 0, 0.3)';
            }}
            >
              Subscribe <ArrowRight size={18} />
            </button>
          </div>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.5)', 
            marginTop: '1.5rem',
            fontSize: '0.9rem'
          }}>
            We respect your privacy. Unsubscribe at any time.
          </p>
        </div>

        {/* Main Footer Content */}
        <div style={{ 
          display: 'grid',
          gap: '3rem',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          marginBottom: '4rem'
        }}>
          {/* Brand Section */}
          <div style={{ maxWidth: '350px' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{
                fontSize: '2rem',
                fontWeight: '800',
                background: colors.gradientSummer,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '0.5rem'
              }}>
                Summer Beat
              </h3>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                <ShieldCheck size={20} color={colors.primary} />
                <span style={{ fontWeight: '500', fontSize: '0.95rem', opacity: 0.9 }}>
                  ISO 9001:2015 Certified
                </span>
              </div>
            </div>
            <p style={{ 
              color: 'rgba(255, 255, 255, 0.75)', 
              marginBottom: '1.5rem', 
              lineHeight: 1.7, 
              fontSize: '1.05rem' 
            }}>
              Quality home appliances for every Indian season. Built for reliability, designed for comfort.
            </p>
            
            {/* Trust Badges */}
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '1rem', 
              marginBottom: '2rem' 
            }}>
              {[
                { icon: <Award size={16} />, text: 'Premium Quality' },
                { icon: <Leaf size={16} />, text: 'Energy Efficient' },
                { icon: <Clock size={16} />, text: '48Hr Support' },
              ].map((badge, i) => (
                <div key={i} style={{
                  background: 'rgba(255, 138, 0, 0.1)',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  border: '1px solid rgba(255, 138, 0, 0.2)'
                }}>
                  {badge.icon}
                  <span style={{ color: colors.primary, fontWeight: '500' }}>{badge.text}</span>
                </div>
              ))}
            </div>

            {/* Contact Info */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'rgba(255, 255, 255, 0.85)' }}>
                <Mail size={18} style={{ color: colors.primary }} />
                <span style={{ fontSize: '0.95rem' }}>summerbeathomeappliance@gmail.com</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'rgba(255, 255, 255, 0.85)' }}>
                
                
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', color: 'rgba(255, 255, 255, 0.85)' }}>
                <MapPin size={18} style={{ color: colors.primary, marginTop: '2px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', lineHeight: 1.5 }}>
                  Khasra No.1160, Sihani Gate<br />
                  Meerut Road, Ghaziabad<br />
                  Uttar Pradesh - 201001
                </span>
              </div>
            </div>
          </div>

          {/* Product Categories */}
          <div>
            <h3 style={{ 
              fontWeight: '700', 
              marginBottom: '1.5rem', 
              fontSize: '1.3rem',
              color: 'white'
            }}>
              Our Products
            </h3>
            <ul style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1rem', 
              listStyle: 'none', 
              padding: 0 
            }}>
              {[
                { label: 'Ceiling Fans', page: 'products' },
                { label: 'Pedestal Fans', page: 'products' },
                { label: 'Air Coolers', page: 'products' },
                { label: 'Fan Heaters', page: 'products' },
                { label: 'Quartz Heaters', page: 'products' },
                { label: 'Mixers & Grinders', page: 'products' }
              ].map((item, i) => (
                <li key={i}>
                  <button 
                    onClick={() => setCurrentPage(item.page)} 
                    style={{ 
                      color: 'rgba(255, 255, 255, 0.75)', 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer', 
                      transition: 'all 0.3s', 
                      textAlign: 'left',
                      padding: '0',
                      fontSize: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }} 
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = colors.primary;
                      e.currentTarget.style.paddingLeft = '0.5rem';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.75)';
                      e.currentTarget.style.paddingLeft = '0';
                    }}
                  >
                    → {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 style={{ 
              fontWeight: '700', 
              marginBottom: '1.5rem', 
              fontSize: '1.3rem',
              color: 'white'
            }}>
              Quick Links
            </h3>
            <ul style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1rem', 
              listStyle: 'none', 
              padding: 0 
            }}>
              {[
                { label: 'Home', page: 'home' },
                { label: 'All Products', page: 'products' },
                { label: 'Contact Us', page: 'contact' },
                { label: 'About Us', page: 'home' },
                { label: 'Warranty Info', page: 'home' },
                { label: 'Service Centers', page: 'contact' }
              ].map((item, i) => (
                <li key={i}>
                  <button 
                    onClick={() => setCurrentPage(item.page)} 
                    style={{ 
                      color: 'rgba(255, 255, 255, 0.75)', 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer', 
                      transition: 'all 0.3s', 
                      textAlign: 'left',
                      padding: '0',
                      fontSize: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }} 
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = colors.primary;
                      e.currentTarget.style.paddingLeft = '0.5rem';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.75)';
                      e.currentTarget.style.paddingLeft = '0';
                    }}
                  >
                    → {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal & Social */}
          <div>
            <h3 style={{ 
              fontWeight: '700', 
              marginBottom: '1.5rem', 
              fontSize: '1.3rem',
              color: 'white'
            }}>
              Information
            </h3>
            
            {/* Legal Links */}
            <ul style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1rem', 
              listStyle: 'none', 
              padding: 0, 
              marginBottom: '2rem' 
            }}>
              {[
                { label: 'Privacy Policy', page: 'home' },
                { label: 'Terms & Conditions', page: 'home' },
                { label: 'Shipping Policy', page: 'home' },
                { label: 'Return Policy', page: 'home' },
                { label: 'Warranty Policy', page: 'home' },
                { label: 'FAQ', page: 'home' }
              ].map((item, i) => (
                <li key={i}>
                  <button 
                    onClick={() => setCurrentPage(item.page)} 
                    style={{ 
                      color: 'rgba(255, 255, 255, 0.75)', 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer', 
                      transition: 'all 0.3s', 
                      textAlign: 'left',
                      padding: '0',
                      fontSize: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }} 
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = colors.primary;
                      e.currentTarget.style.paddingLeft = '0.5rem';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.75)';
                      e.currentTarget.style.paddingLeft = '0';
                    }}
                  >
                    → {item.label}
                  </button>
                </li>
              ))}
            </ul>

            {/* Social Media */}
            <div>
              <p style={{ 
                fontSize: '0.95rem', 
                marginBottom: '1rem', 
                color: 'rgba(255, 255, 255, 0.85)', 
                fontWeight: '600' 
              }}>
                Follow Us
              </p>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {[
                  { icon: <Facebook size={20} />, name: 'Facebook', color: '#1877F2' },
                  { icon: <Instagram size={20} />, name: 'Instagram', color: '#E4405F' },
                  { icon: <Youtube size={20} />, name: 'YouTube', color: '#FF0000' }
                ].map((social, i) => (
                  <button 
                    key={i} 
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 138, 0, 0.2)',
                      color: 'white',
                      width: '44px',
                      height: '44px',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = social.color;
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = social.color;
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = `0 8px 20px ${social.color}40`;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = 'rgba(255, 138, 0, 0.2)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    aria-label={social.name}
                  >
                    {social.icon}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div style={{ 
          borderTop: '1px solid rgba(255, 138, 0, 0.2)', 
          paddingTop: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.7)', 
            fontSize: '0.95rem',
            lineHeight: 1.6
          }}>
            &copy; {new Date().getFullYear()} Summer Beat Home Appliances. All rights reserved.<br />
            ISO 9001:2015 Certified • Made in India 🇮🇳
          </p>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <span style={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              
            </span>
            <span style={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              ⚡ Energy Efficient
            </span>
            <span style={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              
            </span>
          </div>
        </div>

        {/* Company Slogan */}
        <div style={{ 
          marginTop: '2rem',
          paddingTop: '2rem',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center'
        }}>
          <p style={{ 
            fontSize: '1.1rem', 
            color: 'rgba(255, 255, 255, 0.6)',
            fontStyle: 'italic',
            fontWeight: '500'
          }}>
            Cool Summers • Warm Winters • Perfect Comfort
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;